import { SVGs } from "../assets";
import * as React from "react";
import { twMerge } from "tailwind-merge";
import Text from "./Text";
import { useOnClickOutside } from "../hooks";

export type InputOptionsOption = {
  label: string;
  value: any;
  icon?: React.ReactNode;
};
export type InputOptionsProps = {
  children: React.ReactNode;
  value?: string;
  onChange: (value: string, option: InputOptionsOption) => void;
  className?: string;
  classNames?: {
    menu?: string;
  };
  options: InputOptionsOption[];
};

export default function InputOptions({
  children,
  value,
  onChange,
  className,
  options,
  classNames,
}: InputOptionsProps) {
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const itemRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const { open, setOpen } = useOnClickOutside(rootRef);
  const lastInputRef = React.useRef<"mouse" | "keyboard">("mouse");
  const triggerRef = React.useRef<HTMLDivElement | null>(null);

  const getFocusedIndex = React.useCallback(
    () => itemRefs.current.findIndex((x) => x === document.activeElement),
    [],
  );

  const focusIndex = React.useCallback(
    (getNext: (focusedIndex: number) => number) => {
      const idx = getNext(getFocusedIndex());
      const el = itemRefs.current[idx];
      if (el) el.focus();
      else triggerRef.current?.querySelector("input")?.focus();
    },
    [getFocusedIndex, triggerRef],
  );

  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (!options.length || !open) return;
      lastInputRef.current = "keyboard";
      switch (e.key) {
        case "ArrowDown": {
          e.preventDefault();
          focusIndex((c) => c + 1);
          return;
        }
        case "ArrowUp": {
          e.preventDefault();
          focusIndex((c) => c - 1);
          return;
        }
        case "Enter": {
          e.preventDefault();
          itemRefs.current[getFocusedIndex()]?.click();
          return;
        }
        case "Escape": {
          e.preventDefault();
          setOpen(false);
          return;
        }
        default:
          return;
      }
    },
    [open, focusIndex, getFocusedIndex, options.length, setOpen],
  );

  return (
    <div
      ref={rootRef}
      onKeyDown={onKeyDown}
      className={twMerge("relative", className)}
    >
      <div
        ref={triggerRef}
        children={children}
        onMouseDown={() => setOpen(true)}
        onFocusCapture={() => setOpen(true)}
      />
      {open && (
        <div
          role="menu"
          onMouseMove={() => {
            lastInputRef.current = "mouse";
          }}
          className={twMerge(
            "absolute left-0 mt-[10px] z-20 w-full min-w-[150px] overflow-hidden rounded-[4px] bg-surface shadow-md max-h-[300px] overflow-y-auto",
            classNames?.menu,
          )}
        >
          {options.map((opt, idx) => (
            <button
              key={opt.value}
              type="button"
              role="menuitem"
              ref={(el) => {
                itemRefs.current[idx] = el;
              }}
              className={twMerge(
                "flex w-full items-center justify-between px-3 py-[10px] text-sm focus:bg-background outline-none",
              )}
              onMouseEnter={() => {
                if (lastInputRef.current !== "mouse") return;
                focusIndex(() => idx);
              }}
              onClick={() => {
                onChange(opt.value, opt);
                setOpen(false);
              }}
            >
              <div className="flex items-start gap-2">
                {opt.icon}
                <Text className="text-start">{opt.label}</Text>
              </div>
              {opt.value === value && (
                <img
                  src={SVGs.check}
                  alt="Check"
                  className="w-[18px] h-[18px]"
                />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
